(ns ui.enrich.prefill
  "Pure transforms from a completed enrichment result into create-flow inputs.

   The create modal is pre-entity: on resume we rebuild its initial values from
   the frozen request draft (what the user had typed when they dropped the
   document) overlaid with the AI's suggested transaction-process field values.
   The richer slot suggestions (other target entity types) are carried through
   untouched for the create flow to apply after the process exists."
  (:require
   [clojure.string :as str]
   [common.transaction-process.templates.registry :as registry]
   [common.util :as cu]))

(defn- draft->modal-values
  "Map the namespaced request draft onto the create modal's own (un-namespaced)
   form keys."
  [{:keys [request-context]}]
  (let [draft (:draft request-context)]
    {:short-name       (:transaction-process/short-name draft)
     :vehicle-id       (:transaction-process/vehicle draft)
     :template-kw      (or (:template-kw request-context)
                           (:transaction-process/template-kw draft))
     :transaction-type (:transaction-process/transaction-type draft)
     :target-id        (:transaction-process/target draft)}))

(defn transaction-process-fields
  "The AI's suggested field map for the root transaction-process target, or nil.
   Shape: {field-key {:value _ :confidence _ ...}}."
  [result]
  (some-> result :suggestions :targets :transaction-process first :fields))

(defn- suggested-value
  [fields field-key]
  (some-> fields (get field-key) :value))

(defn- spellings
  "The lower-case strings a value may name an option by: \"follow-on\" and
   \":follow-on\" both name :follow-on."
  [value]
  (let [s (some-> value str str/trim str/lower-case)]
    (when (seq s)
      #{s (str/replace s #"^:" "")})))

(defn- transaction-type-of
  "The id of the transaction type of `template-kw` that a suggested `value`
   names by its id or its label, or nil when none does."
  [template-kw value]
  (when-let [named (spellings value)]
    (some (fn [{:transaction-type/keys [id label]}]
            (when (or (some named (spellings id))
                      (contains? named (some-> label str/lower-case)))
              id))
          (:template/transaction-types (registry/get-template template-kw)))))

(defn- apply-suggested-fields
  "Overlay the AI's transaction-process suggestions onto the modal values. Only
   fields the create modal actually edits are applied here; a blank suggestion
   never clobbers a value the user already typed, and a transaction type that
   is not one of the template's keeps the draft's."
  [modal-values fields]
  (let [short-name (suggested-value fields :transaction-process/short-name)
        txn-type   (transaction-type-of
                    (:template-kw modal-values)
                    (suggested-value fields :transaction-process/transaction-type))]
    (cond-> modal-values
      (cu/non-empty-string? short-name) (assoc :short-name short-name)
      (some? txn-type)                  (assoc :transaction-type txn-type))))

(defn create-modal-values
  "Build the create modal's :initial-values from a completed enrichment result:
   the frozen request draft, overlaid with the AI's suggested transaction-process
   field values. Nil values are dropped so the modal keeps its own defaults."
  [result]
  (-> (draft->modal-values result)
      (apply-suggested-fields (transaction-process-fields result))
      (cu/remove-nil-vals)))
